/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    FiShoppingBag,
    FiMapPin,
    FiPrinter,
    FiArrowRight,
    FiArrowLeft
} from "react-icons/fi";
import { useUI } from "../../theme/ThemeProvider";
import { pickActiveDeliveryCompany } from "../../../Stores/utils/storeApi";
import { getTenantLogoUrl } from "../../../utils/getTenantLogoUrl";
const orderStatuses = [
    { id: 1, ar: "جديد", en: "New", key: "new", color: "bg-blue-100 text-blue-700 border-blue-200" },
    { id: 2, ar: "جاري التجهيز", en: "Preparing", key: "preparing", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    { id: 3, ar: "جاهز", en: "Ready", key: "ready", color: "bg-purple-100 text-purple-700 border-purple-200" },
    { id: 4, ar: "جاري التوصيل", en: "In Delivery", key: "delivering", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
    { id: 5, ar: "مكتمل", en: "Completed", key: "delivered", color: "bg-green-100 text-green-700 border-green-200" },
    { id: 6, ar: "مسترجع", en: "Returned", key: "returned", color: "bg-orange-100 text-orange-700 border-orange-200" },
    { id: 8, ar: "مسترجع جزئيًا", en: "Partially Returned", key: "partially_returned", color: "bg-teal-100 text-teal-700 border-teal-200" },
    { id: 7, ar: "ملغي", en: "Cancelled", key: "cancelled", color: "bg-red-100 text-red-700 border-red-200" },
    { id: 9, ar: "فشل التوصيل", en: "Delivery Failed", key: "failed", color: "bg-black text-white border-gray-800" },
];
export default function PrintInvoice() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { lang } = useUI();
    const isArabic = lang === "ar";
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deliveryInfo, setDeliveryInfo] = useState(null);
    const [invoiceSettings, setInvoiceSettings] = useState({
        header: true,
        footer: true,
        issueDate: true,
        showProductImages: false,
        showPricesNextToItems: true
    });
    const [storeInfo, setStoreInfo] = useState({ name: "", address: "" });

    const baseURL = (
        process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api"
    ).replace(/\/+$/, "");

    const token = localStorage.getItem("authToken");

    useEffect(() => {
        if (!orderId) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const tenantId = localStorage.getItem("tenantId");

                // Fetch Order Details
                const orderRes = await fetch(`${baseURL}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`, {
                    headers: { Authorization: token ? `Bearer ${token}` : "" }
                });
                if (orderRes.ok) {
                    const orders = await orderRes.json();
                    const target = orders.find(o => o.orderId === orderId);
                    setOrder(target);

                    // Fetch delivery info by cityId
                    if (target?.cityId) {
                        try {
                            const delRes = await fetch(
                                `${baseURL}/TenantDelivery/GetByCity?tenantId=${tenantId}&cityId=${target.cityId}`,
                                { headers: { Authorization: token ? `Bearer ${token}` : "" } }
                            );
                            if (delRes.ok) {
                                const delData = await delRes.json();
                                const activeDelivery = pickActiveDeliveryCompany(delData);
                                if (activeDelivery) {
                                    setDeliveryInfo({
                                        name: activeDelivery.deliveryCompanyName,
                                        fee: Number(activeDelivery.deliveryCompanyFees ?? 0)
                                    });
                                }
                            }
                        } catch (e) {
                            console.warn("Delivery fetch error:", e);
                        }
                    }
                }

                // Fetch Invoice Settings
                const settingsRes = await fetch(`${baseURL}/InvoiceSetting/get?tenantId=${tenantId}`, {
                    headers: { Authorization: token ? `Bearer ${token}` : "" }
                });
                if (settingsRes.ok) {
                    const settings = await settingsRes.json();
                    setInvoiceSettings(settings);
                }
            } catch (err) {
                console.error("Failed to fetch data", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [orderId, baseURL, token]);

    useEffect(() => {
        const getStoreInfo = () => {
            let storeData = {};
            let tenantDetails = {};
            try {
                storeData = JSON.parse(localStorage.getItem("storeData")) || {};
            } catch (e) {
                storeData = {};
            }
            try {
                tenantDetails = JSON.parse(localStorage.getItem("tenantDetails")) || {};
            } catch (e) {
                tenantDetails = {};
            }

            // Name logic from Sidebar
            let name = isArabic
                ? (storeData.TenantName || storeData.EngTenantName || tenantDetails.TenantName || tenantDetails.EngTenantName)
                : (storeData.EngTenantName || storeData.TenantName || tenantDetails.EngTenantName || tenantDetails.TenantName);

            if (!name) {
                name = localStorage.getItem("businessName");
            }

            if (!name) {
                try {
                    const tenantStr = localStorage.getItem("tenant");
                    if (tenantStr) {
                        const tenant = JSON.parse(tenantStr);
                        name = tenant.BusinessName || tenant.businessName || tenant.ClientName || "";
                    }
                } catch (e) { }
            }

            // Address logic
            const address = isArabic
                ? (storeData.Address || storeData.TenantAddress || tenantDetails.Address || "العراق، بغداد")
                : (storeData.EngAddress || storeData.Address || tenantDetails.EngAddress || "Baghdad, Iraq");

            // Logo logic
            const logoPath =
                storeData.Logo ||
                storeData.logo ||
                storeData.logoFile ||
                tenantDetails.Logo ||
                localStorage.getItem("companyLogo") ||
                "";
            const logoUrl = getTenantLogoUrl(logoPath);

            return {
                name: name || (isArabic ? "متجري" : "My Store"),
                address: address,
                logo: logoUrl
            };
        };

        setStoreInfo(getStoreInfo());
    }, [isArabic]);

    const parseOrderDate = (dateStr) => {
        if (!dateStr) return "—";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleString(isArabic ? 'ar-EG' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const calculatedSubtotal = (order?.details || []).length > 0
        ? (order?.details || []).reduce((acc, item) => acc + (Number(item.lineTotal) || 0), 0)
        : Number(order?.totalAmount || 0);
    const discountAmount = Number(order?.discountAmount || 0);
    const shippingCost = deliveryInfo ? deliveryInfo.fee : Math.max(0, Number(order?.netAmount || 0) - (calculatedSubtotal - discountAmount));
    const grandTotal = calculatedSubtotal - discountAmount + shippingCost;

    const formatPrice = (val) => {
        return `${Number(val || 0).toLocaleString()} ${isArabic ? "د.ع" : "IQD"}`;
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        </div>
    );

    if (!order) return <div className="p-20 text-center font-bold">Order not found</div>;

    return (
        <div className="min-h-screen bg-gray-100 p-4 md:p-8 print:p-0 print:bg-white" dir="rtl">
            {/* Control Bar */}
            <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between print:hidden">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-white border border-gray-200 font-bold text-xs md:text-sm "
                >
                    {isArabic ? <FiArrowRight /> : <FiArrowLeft />}
                    {isArabic ? "رجوع" : "Back"}
                </button>
                <button
                    onClick={() => window.print()}
                    className="flex items-center gap-2 px-4 md:px-6 py-2.5 rounded-xl bg-purple-600 text-white font-bold text-xs md:text-sm shadow-lg "
                >
                    <FiPrinter />
                    {isArabic ? "طباعة الفاتورة" : "Print Invoice"}
                </button>
            </div>

            {/* Invoice Page Body */}
            <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-2xl md:rounded-3xl overflow-hidden print:shadow-none print:rounded-none">
                <div id="printable-invoice" className="p-6 md:p-16 space-y-8 md:space-y-12 text-end">
                    {/* Header Section */}
                    {invoiceSettings.header && (
                        <div className="flex flex-col items-center border-b-[6px] border-purple-800 pb-10">
                            <div className="w-24 h-24 mb-4 flex items-center justify-center">
                                {storeInfo.logo ? (
                                    <img src={storeInfo.logo} className="w-full h-full object-contain" alt="Logo" />
                                ) : (
                                    <svg viewBox="0 0 100 100" className="w-full h-full text-purple-800 fill-current">
                                        <path d="M50 0C22.4 0 0 22.4 0 50s22.4 50 50 50 50-22.4 50-50S77.6 0 50 0zm0 90C27.9 90 10 72.1 10 50S27.9 10 50 10s40 17.9 40 40-17.9 40-40 40z" />
                                        <circle cx="50" cy="50" r="15" />
                                        <path d="M50 25c-13.8 0-25 11.2-25 25s11.2 25 25 25 25-11.2 25-25-11.2-25-25-25zm0 40c-8.3 0-15-6.7-15-15s6.7-15 15-15 15 6.7 15 15-6.7 15-15 15z" />
                                    </svg>
                                )}
                            </div>
                            <h2 className="text-xl font-black uppercase text-gray-800 tracking-widest">{storeInfo.name}</h2>

                            <div className="mt-6 flex flex-col items-center">
                                <div className="h-12 w-48 bg-black flex items-center justify-between px-1 gap-[2px]">
                                    {[...Array(30)].map((_, i) => (
                                        <div key={i} className="bg-white h-full" style={{ width: `${Math.random() * 4 + 1}px` }} />
                                    ))}
                                </div>
                                <span className="text-sm font-bold mt-1 tracking-[4px]">{order.orderId}</span>
                            </div>
                        </div>
                    )}

                    {/* Client & Order Info Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 md:gap-y-6 text-sm">
                        <div className="space-y-4">
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500 font-bold">{isArabic ? "اسم العميل:" : "Customer Name:"}</span>
                                <span className="font-extrabold">{order.customerName}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500 font-bold">{isArabic ? "رقم الهاتف:" : "Phone:"}</span>
                                <span className="font-extrabold ltr">{order.customerPhone1}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500 font-bold">{isArabic ? "طريقة الدفع:" : "Payment Method:"}</span>
                                <span className="font-extrabold">{order.paymentMethodName || "Cash"}</span>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500 font-bold">{isArabic ? "رقم الفاتورة:" : "Invoice No:"}</span>
                                <span className="font-extrabold text-purple-700">#{order.orderNo}</span>
                            </div>
                            {invoiceSettings.issueDate && (
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-gray-500 font-bold">{isArabic ? "تاريخ الطلب:" : "Order Date:"}</span>
                                    <span className="font-extrabold">{parseOrderDate(order.orderDate)}</span>
                                </div>
                            )}
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500 font-bold">{isArabic ? "حالة الطلب:" : "Order Status:"}</span>
                                <span className="font-extrabold text-green-600">
                                    {(() => {
                                        // نبحث عن الحالة باستخدام الـ statusId القادم من الـ API
                                        const statusObj = orderStatuses.find(s => s.id === Number(order.statusId));

                                        if (statusObj) {
                                            return isArabic ? statusObj.ar : statusObj.en;
                                        }

                                        // إذا لم يجد الـ ID، نستخدم منطق الـ includes اللي إنت بعته كـ Fallback
                                        const v = (order.status || "").toLowerCase();
                                        if (["pending", "قيد المعالجة", "new", "جديد"].includes(v)) return isArabic ? "جديد" : "New";
                                        if (["قيد التجهيز", "تم التجهيز", "preparing", "prepared"].includes(v)) return isArabic ? "جاري التجهيز" : "Preparing";
                                        if (["ready", "جاهز"].includes(v)) return isArabic ? "جاهز" : "Ready";
                                        if (["جاري التوصيل", "delivering", "in delivery"].includes(v)) return isArabic ? "جاري التوصيل" : "Delivering";
                                        if (["واصل", "delivered", "completed", "مكتمل"].includes(v)) return isArabic ? "مكتمل" : "Completed";
                                        if (v.includes("partially") || v.includes("جزئي")) return isArabic ? "مسترجع جزئيًا" : "Partially Returned";
                                        if (v.includes("راجع") || v.includes("returned")) return isArabic ? "مرتجع" : "Returned";
                                        if (["ملغي", "cancelled", "canceled"].includes(v)) return isArabic ? "ملغي" : "Cancelled";
                                        if (["فشل التوصيل", "failed", "delivery failed"].includes(v)) return isArabic ? "فشل التوصيل" : "Delivery Failed";

                                        return order.status; // إذا فشل كل شيء، اعرض النص كما هو من الـ API
                                    })()}
                                </span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span className="text-gray-500 font-bold">{isArabic ? "عنوان الشحن:" : "Shipping Address:"}</span>
                                <span className="font-extrabold">{order.address || order.city || (isArabic ? "العراق" : "Iraq")}</span>
                            </div>
                        </div>
                    </div>

                    {/* Items Table */}
                    <div className="mt-8 md:mt-12 overflow-x-auto no-scrollbar">
                        <h3 className="text-lg font-black mb-6 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500"><FiShoppingBag size={18} /></span>
                            {isArabic ? "المنتجات" : "Items"}
                        </h3>
                        <table className="w-full text-sm border-collapse min-w-[500px]">
                            <thead>
                                <tr className="bg-gray-50 border-y-2 border-gray-100">
                                    {invoiceSettings.showProductImages && (
                                        <th className="py-4 px-4 text-center w-20">{isArabic ? "الصورة" : "Image"}</th>
                                    )}
                                    <th className="py-4 px-4 text-start">{isArabic ? "المنتج" : "Product"}</th>
                                    <th className="py-4 px-4 text-center">{isArabic ? "الكمية" : "Qty"}</th>
                                    {invoiceSettings.showPricesNextToItems && (
                                        <th className="py-4 px-4 text-center">{isArabic ? "السعر" : "Price"}</th>
                                    )}
                                    <th className="py-4 px-4 text-end">{isArabic ? "الإجمالي" : "Total"}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {(order.details || []).map((item, idx) => (
                                    <tr key={idx} className="">
                                        {invoiceSettings.showProductImages && (
                                            <td className="py-5 px-4 text-center">
                                                <div className="w-12 h-12 rounded-lg border border-gray-100 overflow-hidden bg-white flex items-center justify-center p-1 mx-auto">
                                                    {item.imageUrl || item.mainImage ? (
                                                        <img
                                                            src={(() => {
                                                                const src = item.imageUrl || item.mainImage;
                                                                if (!src) return "";
                                                                if (src.startsWith("http")) return src;
                                                                const cleanBase = baseURL.replace("/api", "");
                                                                const parts = src.replace(/^\/+/, "").split("/");
                                                                if (parts.length >= 3 && (parts[0] === "TenantDtl" || parts[0] === "Logo")) {
                                                                    return `${cleanBase}/uploads/${parts[1]}/${parts[0]}/${parts[2]}`;
                                                                }
                                                                return `${cleanBase}${src.startsWith("/") ? "" : "/"}${src}`;
                                                            })()}
                                                            className="w-full h-full object-contain"
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <FiShoppingBag className="text-gray-200" size={20} />
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        <td className="py-5 px-4 font-black">
                                            {item.itemName}
                                            <span className="block text-[10px] text-gray-400 mt-1 font-mono uppercase tracking-tighter">SKU: {item.itemId}</span>
                                        </td>
                                        <td className="py-5 px-4 text-center font-bold">x{item.quantity}</td>
                                        {invoiceSettings.showPricesNextToItems && (
                                            <td className="py-5 px-4 text-center">{formatPrice(item.price)}</td>
                                        )}
                                        <td className="py-5 px-4 text-end font-black text-purple-700">{formatPrice(item.lineTotal)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Totals Section */}
                    <div className="flex flex-col items-end pt-10 mt-10 border-t-2 border-dashed border-gray-200">
                        <div className="w-full max-w-[350px] space-y-4">
                            <div className="flex justify-between text-base">
                                <span className="text-gray-400 font-bold">{isArabic ? "قيمة المنتجات" : "Products Value"}</span>
                                <span className="font-extrabold">{formatPrice(calculatedSubtotal)}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className="flex justify-between text-base text-red-500">
                                    <span className="font-bold">{isArabic ? "الخصم" : "Discount"}</span>
                                    <span className="font-extrabold">-{formatPrice(discountAmount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-base">
                                <span className="text-gray-400 font-bold">
                                    {isArabic ? "الشحن" : "Shipping"}
                                    {deliveryInfo?.name ? (
                                        <span className="block text-[10px] text-purple-600 font-bold mt-0.5">{deliveryInfo.name}</span>
                                    ) : null}
                                </span>
                                <span className={shippingCost === 0 ? "text-green-500 font-extrabold" : "font-extrabold ltr"}>
                                    {shippingCost === 0 ? (isArabic ? "مجاني" : "Free") : formatPrice(shippingCost)}
                                </span>
                            </div>
                            <div className="pt-4 border-t-2 border-purple-800 flex justify-between items-center bg-gray-50 p-4 rounded-2xl">
                                <span className="text-xl font-black text-purple-900">{isArabic ? "المجموع الكلي" : "Grand Total"}</span>
                                <span className="text-2xl font-black text-purple-800">{formatPrice(grandTotal)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Footer Section */}
                    {invoiceSettings.footer && (
                        <div className="mt-20 pt-10 border-t border-gray-100 flex flex-col items-center gap-3 text-gray-400">
                            <div className="flex items-center gap-2">
                                <FiMapPin size={14} />
                                <span className="text-xs font-bold font-black">{isArabic ? `عنوان المتجر: ${storeInfo.address}` : `Store Address: ${storeInfo.address}`}</span>
                            </div>
                            <p className="text-[10px] font-medium opacity-60">© {new Date().getFullYear()} {storeInfo.name}</p>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
        @media print {
          body { background: white !important; margin: 0; padding: 0; }
          .print\\:hidden { display: none !important; }
          #printable-invoice { width: 100% !important; margin: 0 !important; padding: 0 !important; }
          .max-w-4xl { max-width: 100% !important; width: 100% !important; margin: 0 !important; }
          .shadow-2xl, .shadow-none { box-shadow: none !important; }
          .rounded-3xl, .rounded-none { border-radius: 0 !important; }
        }
      `}</style>
        </div>
    );
}
